import { Amplify } from 'aws-amplify'
import {
  confirmSignIn,
  fetchAuthSession,
  getCurrentUser,
  signIn,
  signInWithRedirect,
  signOut as amplifySignOut,
} from 'aws-amplify/auth'
import { logger } from 'services/logger'
import { LOGIN_CALLBACK_URL } from '../../config/config'
import { type CognitoUser } from './cognito.types'
import { type KeysValue } from '../KeysProvider'
// –––––––––––––––––––––––––––––––––––––––––––––––––––––––––––
// Amplify / Cognito configuration
// Note: All values are placeholders – update via env / runtime keys.
// https://www.notion.so/mixtiles/Migrating-Auth0-to-AWS-cognito-1fa09f38c373802bb038e3d6e72276f7
// –––––––––––––––––––––––––––––––––––––––––––––––––––––––––––

export const initCognito = (keys: KeysValue) => {
  const USER_POOL_ID = keys.cognitoUserPoolId || ''
  const USER_POOL_CLIENT_ID = keys.cognitoWebClientId || ''
  const COGNITO_DOMAIN = keys.cognitoDomain || ''

  if (!USER_POOL_ID || !USER_POOL_CLIENT_ID || !COGNITO_DOMAIN) {
    logger.error('Missing Cognito configuration', {
      USER_POOL_ID,
      USER_POOL_CLIENT_ID,
      COGNITO_DOMAIN,
    })
  }

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: USER_POOL_ID,
        userPoolClientId: USER_POOL_CLIENT_ID,
        loginWith: {
          oauth: {
            domain: COGNITO_DOMAIN,
            scopes: ['email', 'openid', 'profile'],
            redirectSignIn: [
              typeof window !== 'undefined'
                ? `${window.location.origin}${LOGIN_CALLBACK_URL}`
                : '',
            ],
            redirectSignOut: [
              typeof window !== 'undefined'
                ? `${window.location.origin}/logout`
                : '',
            ],
            responseType: 'code',
          },
          email: true,
        },
      },
    },
  })
}

// –––––––––––––––––––––––––––––––––––––––––––––––––––––––––––
// Facade that mirrors the old AuthProvider helpers
// –––––––––––––––––––––––––––––––––––––––––––––––––––––––––––

export async function initiateEmailSignIn(
  email: string,
  signUpRequestCb: () => Promise<any>
) {
  try {
    const { nextStep: signInNextStep } = await signIn({
      username: email,
      options: { authFlowType: 'USER_AUTH', preferredChallenge: 'EMAIL_OTP' },
    })

    logger.info('SignIn response', {
      email,
      signInStep: signInNextStep.signInStep,
    })

    // If we get here, user exists and we got a valid response
    if (signInNextStep.signInStep !== 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE') {
      // Case 1: "Prevent user existence errors" is enabled AND user doesn't exist
      // Case 2: "Prevent user existence errors" is disabled AND user doesn't exist (shouldn't reach here, but just in case)
      logger.info('User does not exist, creating user', { email })
      await signUpRequestCb()

      const retryResult = await signIn({
        username: email,
        options: { authFlowType: 'USER_AUTH', preferredChallenge: 'EMAIL_OTP' },
      })

      logger.info('SignIn successful after user creation', {
        email,
        signInStep: retryResult.nextStep.signInStep,
      })

      return retryResult
    }

    return signInNextStep
  } catch (e) {
    // Check if this is a "user does not exist" error
    if (
      e instanceof Error &&
      (e.name === 'UserNotFoundException' ||
        e.message?.includes('User does not exist') ||
        e.message?.includes('UserNotFoundException'))
    ) {
      // Case 3: User appears to exist (got EMAIL_OTP challenge)
      // This could be a real user OR a problematic case with "Prevent user existence errors" enabled
      logger.info('User appears to exist, got EMAIL_OTP challenge', { email })

      try {
        await signUpRequestCb()

        // Now try signing in again with the newly created user
        const { nextStep: retrySignInStep } = await signIn({
          username: email,
          options: {
            authFlowType: 'USER_AUTH',
            preferredChallenge: 'EMAIL_OTP',
          },
        })

        logger.info('SignIn successful after user creation', {
          email,
          signInStep: retrySignInStep.signInStep,
        })

        return retrySignInStep
      } catch (createError) {
        logger.error(
          'Failed to create user or sign in after creation',
          createError,
          { email }
        )
        throw createError
      }
    } else {
      // Some other error, re-throw it
      logger.error('Failed to initiate email sign in', e, { email })
      throw e
    }
  }
}

export async function confirmEmailCode(_user: any, code: string) {
  return confirmSignIn({ challengeResponse: code })
}

export const getAccessToken = async (): Promise<string | null> => {
  try {
    const session = await fetchAuthSession()
    return session.tokens?.accessToken?.toString() || null
  } catch (err) {
    logger.info('No session for getAccessToken', { err })
    return null
  }
}
export const getIdToken = async (): Promise<string | null> => {
  try {
    const session = await fetchAuthSession()
    return session.tokens?.idToken?.toString() || null
  } catch (err) {
    logger.info('No session for getAccessToken', { err })
    return null
  }
}

async function getUserAttributesFromToken() {
  const idToken = await getIdToken()
  if (idToken) {
    return JSON.parse(atob(idToken.split('.')[1]))
  } else {
    return {}
  }
}

export const signInWithGoogle = () => signInWithRedirect({ provider: 'Google' })
export const signInWithApple = () => signInWithRedirect({ provider: 'Apple' })

export const getUser = async (): Promise<CognitoUser> => {
  const user = await getCurrentUser()
  const attributes = await getUserAttributesFromToken()
  return { ...user, ...attributes }
}

export const signOut = () => amplifySignOut()
